import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { listLanguage } from '../../../../configs/LanguageConfig';
import ChartCanvas from '../../../common/chart/ChartCanvas';
import { Chart } from 'react-stockcharts';
import { AreaSeries } from 'react-stockcharts/lib/series';
import LineSeries from './lib/LineSeries';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import moment from 'moment';
import {
  formatTextFloat,
  formatValue,
  formatValueBillion,
} from './../../../helpers/Text';
import MouseCoordinateX from '../../../common/chart/MouseCoordinateX';
import HoverTooltip from './../../../common/chart/lib/tooltip/HoverTooltip';
import { CrossHairCursor } from 'react-stockcharts/lib/coordinates';
import { I18n } from 'react-redux-i18n';
import {
  getTickValues,
  getYExtentsChart,
  tickIntervalFunction,
} from './../../../helpers/Chart';
import { CurrentCoordinate } from './../../../common/chart';
import { THEMES } from './../../../../configs/LayoutConfig';
import withSwitchTheme from '../../../common/withSwitchTheme';

const SETTINGS_COMMON = {
  MARGIN: { left: 20, right: 30, top: 10, bottom: 30 },
  WHITE_COLOR: '#ffffff',
  TICK_INTERVAL: 5,
  TICK_VALUES: 5,
  RED_COLOR: '#eb505a',
  RED_COLOR_B: '#e63946',
  ORANGE_COLOR: '#facc5c',
  BLUE_COLOR: '#2a78d1',
  BLUE_COLOR_B: '#47c3ff',
  BLUE_COLOR_AREA: '#1d97ef',
  GREY_COLOR: '#555555',
  GREY_COLOR_B: '#1e242e',
  COLOR_TEXT: '#ffffff',
};

const SETTINGS = {
  [THEMES.DARK]: {
    ...SETTINGS_COMMON,
  },
  [THEMES.LIGHT]: {
    ...SETTINGS_COMMON,
    RED_COLOR: '#e94235',
    BLUE_COLOR_B: '#2c73e8',
    ORANGE_COLOR: '#f8bc06',
    COLOR_TEXT: '#1f2023',
  },
  [THEMES.CLASSIC]: {
    ...SETTINGS_COMMON,
    ORANGE_COLOR: '#f8bc06',
    COLOR_TEXT: '#ffffff',
  },
};

class ChartContent extends React.Component {
  constructor(props) {
    super(props);
    const { isFormatBillion } = this.props;
    this.formatter = isFormatBillion ? formatValueBillion : formatValue;
  }

  getItemMinMax = item => {
    const { foreignBuyValue, foreignSellValue } = item;

    return {
      min: Math.min(
        foreignBuyValue,
        foreignSellValue,
        foreignBuyValue - foreignSellValue,
      ),
      max: Math.max(
        foreignBuyValue,
        foreignSellValue,
        foreignBuyValue - foreignSellValue,
      ),
    };
  };

  getMinMaxData = data => {
    if (data.length < 1) {
      return {
        min: 0,
        max: 0,
      };
    }

    let { min, max } = this.getItemMinMax(data[0]);

    const length = data.length;
    for (let index = 1; index < length; index++) {
      const { min: tmpMin, max: tmpMax } = this.getItemMinMax(data[index]);
      min = Math.min(tmpMin, min);
      max = Math.max(tmpMax, max);
    }
    return {
      min,
      max,
    };
  };

  getGridWidth = () => {
    const { width } = this.props;
    const margin = this.getDynamicMargin();

    return width - margin.left - margin.right;
  };

  tooltipContent = SETTINGS => {
    const { i18n, isFormatBillion } = this.props;
    let suffix = ' Bn.';

    if (i18n.locale === listLanguage.en) {
      suffix = isFormatBillion ? ' Bn.' : ' Mil.';
    } else {
      suffix = isFormatBillion ? ' Tỷ' : ' Tr.';
    }

    return ({ currentItem, xAccessor }) => {
      if (currentItem.foreignBuyValue || currentItem.foreignSellValue) {
        return {
          x: '',
          y: [
            {
              label: I18n.t('moneyFlowForeign.total_foreign_buy_value'),
              value:
                formatTextFloat(this.formatter(currentItem.foreignBuyValue)) +
                suffix,
              stroke: SETTINGS.BLUE_COLOR,
            },
            {
              label: I18n.t('moneyFlowForeign.total_foreign_sell_value'),
              value:
                formatTextFloat(this.formatter(currentItem.foreignSellValue)) +
                suffix,
              stroke: SETTINGS.ORANGE_COLOR,
            },
            {
              label: I18n.t('moneyFlowForeign.net_foreign_value'),
              value:
                formatTextFloat(
                  this.formatter(
                    currentItem.foreignBuyValue - currentItem.foreignSellValue,
                  ),
                ) + suffix,
              stroke: SETTINGS.RED_COLOR_B,
            },
          ],
        };
      }
    };
  };

  getDynamicMargin = () => {
    const { SETTINGS, data } = this.props;
    const { max: yMax } = this.getMinMaxData(data);
    const marginLeft = Math.max(
      formatTextFloat(this.formatter(yMax)).toString().length * 7,
      SETTINGS.MARGIN.left,
    );

    return {
      ...SETTINGS.MARGIN,
      left: marginLeft,
    };
  };

  render() {
    const { data, width, height, ratio, SETTINGS } = this.props;

    const { max: yMax, min: yMin } = this.getMinMaxData(data);

    return (
      <ChartCanvas
        width={width}
        height={height}
        data={data}
        ratio={ratio}
        margin={this.getDynamicMargin()}
      >
        <Chart
          id={1}
          yExtents={getYExtentsChart(
            this.formatter(yMin),
            this.formatter(yMax),
            5,
          )}
        >
          <YAxis
            axisAt="left"
            orient="left"
            ticks={5}
            tickStroke={SETTINGS.GREY_COLOR}
            tickStrokeOpacity={0.5}
            showDomain={false}
            zoomEnabled={false}
            outerTickSize={0}
            edgeClip={true}
            showTicks={true}
            showTickLabel={false}
            innerTickSize={-1 * this.getGridWidth()}
            tickIntervalFunction={tickIntervalFunction}
          />

          <YAxis
            axisAt="left"
            orient="left"
            ticks={5}
            tickStroke={SETTINGS.COLOR_TEXT}
            showDomain={false}
            zoomEnabled={false}
            outerTickSize={0}
            edgeClip={true}
            showTicks={true}
            innerTickSize={0}
            tickIntervalFunction={tickIntervalFunction}
          />

          <XAxis
            axisAt="bottom"
            orient="bottom"
            innerTickSize={0}
            tickStroke={SETTINGS.COLOR_TEXT}
            tickValues={getTickValues(data.length, SETTINGS.TICK_VALUES)}
            zoomEnabled={false}
            showDomain={false}
            tickPadding={10}
            tickFormat={d => {
              const tradingDate = moment(data[d].tradingDate);
              return tradingDate.format('HH:mm').toString();
            }}
          />

          <LineSeries
            yAccessor={d => this.formatter(d.foreignBuyValue)}
            stroke={SETTINGS.BLUE_COLOR_B}
            field={'foreignBuyValue'}
          />

          <LineSeries
            yAccessor={d => this.formatter(d.foreignSellValue)}
            stroke={SETTINGS.ORANGE_COLOR}
            field={'foreignSellValue'}
          />

          <AreaSeries
            yAccessor={d => {
              return (
                this.formatter(d.foreignBuyValue - d.foreignSellValue) <= 0 &&
                this.formatter(d.foreignBuyValue - d.foreignSellValue)
              );
            }}
            fill={SETTINGS.RED_COLOR}
            strokeOpacity={0}
            stroke={SETTINGS.RED_COLOR}
            baseAt={scale => scale(0)}
          />

          <AreaSeries
            yAccessor={d => {
              return (
                this.formatter(d.foreignBuyValue - d.foreignSellValue) > 0 &&
                this.formatter(d.foreignBuyValue - d.foreignSellValue)
              );
            }}
            fill={SETTINGS.BLUE_COLOR_AREA}
            strokeOpacity={0}
            stroke={SETTINGS.RED_COLOR}
            baseAt={scale => scale(0)}
          />

          <MouseCoordinateX timeFormat="%H:%M %m/%d/%Y" />

          <HoverTooltip
            bgOpacity={0}
            stroke={SETTINGS.GREY_COLOR}
            fill={SETTINGS.WHITE_COLOR}
            opacity={1}
            tooltipContent={this.tooltipContent(SETTINGS)}
            tooltipCanvas={tooltipCanvas()}
            fontSize={11}
          />
          <CrossHairCursor stroke={SETTINGS.WHITE_COLOR} />
          <CurrentCoordinate
            field={'foreignBuyValue'}
            yAccessor={d => this.formatter(d.foreignBuyValue)}
          />
          <CurrentCoordinate
            field={'foreignSellValue'}
            yAccessor={d => this.formatter(d.foreignSellValue)}
            color="rgba(250, 204, 92, 0.15)"
          />
        </Chart>
      </ChartCanvas>
    );
  }
}

function tooltipCanvas(_ref, _isSynchronizingTooltip, _eventSynchTooltipName) {
  const LINE_SPACE = 4;

  return (_ref5, content, ctx, pointer, height, moreProps, _this) => {
    if (_isSynchronizingTooltip) {
      if (typeof _ref !== 'undefined') {
        _ref(moreProps, _eventSynchTooltipName, true);
      }
    }
    const X = 10;
    // const Y = 0;
    var fontFamily = _ref5.fontFamily,
      fontSize = _ref5.fontSize,
      fontFill = _ref5.fontFill;
    var startY = 0;
    ctx.font = 'bold ' + fontSize + 'px ' + fontFamily;
    for (var i = 0; i < content.y.length; i++) {
      var y = content.y[i];
      var textY = startY + fontSize * (i + 1) + (i + 1) * LINE_SPACE;
      ctx.fillStyle = y.stroke || fontFill;
      ctx.fillText(y.label, X, textY);
      ctx.fillStyle = fontFill;
      ctx.fillText(y.value, X + ctx.measureText(y.label).width, textY);
    }
  };
}

ChartContent.propTypes = {
  data: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  SETTINGS: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  i18n: state.i18n,
});

export default compose(
  withSwitchTheme(SETTINGS),
  connect(mapStateToProps),
)(ChartContent);
